import DashLogo from "assets/images/png/SignInLogo.png";
import { Icon, NotFoundPage, PaginationContext, Spinner } from "dash-ui";
import {
  IdxSettings,
  Property,
  useGetIdxEmbeddedPortfolioFeedQuery,
  useGetIdxPortfolioFeedPropertiesQuery,
} from "gql/generated.graphql";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiLoader,
  FiMapPin,
} from "react-icons/fi";
import { Link, useLocation, useParams } from "react-router-dom";
import { lighten } from "polished";
import { ExchangeMapView } from "v2/components/idx/exchange-map-view";
import { DEFAULT_PROPERTIES_PER_PAGE } from "v2/components/idx/constants/constants";
import { IdxPreviousPageState } from "./idx-embedded-search-page";
import UnavailablePortfolioPage from "pages/portfolios/UnavailablePortfolioPage/UnavailablePortfolioPage";
import { ServerErrors } from "dash-common";

type SelectedView = "GRID" | "MAP";

// Main Function
export function IdxEmbeddedPortfolioPage() {
  const location = useLocation();

  // The state of the portfolio page before clicking on a property
  const [previousState, setPreviousState] =
    useState<IdxPreviousPageState | null>(location?.state);

  // The previous state of the map view
  const previousMapState = previousState?.mapState;
  const previousPaginationState = previousState?.pagination;
  const previousSelectedPage = previousPaginationState?.selectedPage;
  const previousSelectedLimit = previousPaginationState?.limit;
  const previousSelectedView = previousState?.type;

  // Pagination context
  const { setTotal, state, getRefetchProps, setPerPage } =
    useContext(PaginationContext);
  const { limit } = getRefetchProps(state);

  const [selectedView, setSelectedView] = useState<SelectedView>(
    previousSelectedView ?? "GRID"
  );

  const [skip, setSkip] = useState<number>(
    previousSelectedPage
      ? (previousSelectedPage - 1) *
          (previousSelectedLimit || limit || DEFAULT_PROPERTIES_PER_PAGE)
      : 0
  );

  // Route params
  const params = useParams();
  const idxFeedId = params.idxFeedId || "";
  const portfolioFolderId = params.portfolioId || "";

  // Portfolio Feed Data
  const {
    data: getIdxData,
    loading: loadingSettings,
    error: getIdxError,
  } = useGetIdxEmbeddedPortfolioFeedQuery({
    variables: {
      input: { idxFeedId },
    },
    onCompleted: (data) => {
      const initialView =
        data.getIdxEmbeddedPortfolioFeed.idxFeed.portfolioContent ===
        "LIST_IN_MAP"
          ? "MAP"
          : "GRID";
      setSelectedView(previousSelectedView ?? initialView);
    },
  });

  // Portfolio Properties Data
  const {
    data: getPropertiesData,
    loading: loadingProperties,
    error: getPropertiesError,
  } = useGetIdxPortfolioFeedPropertiesQuery({
    variables: {
      input: { idxFeedId, portfolioFolderId, pagination: { skip, limit } },
    },
    onCompleted: (data) => {
      const total = data.getIdxPortfolioFeedProperties.portfolioItemCount || 0;
      setTotal(total);
    },
  });

  const broker = getPropertiesData?.getIdxPortfolioFeedProperties.broker;
  const idxFeed = getIdxData?.getIdxEmbeddedPortfolioFeed.idxFeed;
  const portfolioItems =
    getPropertiesData?.getIdxPortfolioFeedProperties.portfolioItems || [];
  const properties = portfolioItems.map((item) => item.property) as Property[];

  const idxSettings =
    (getIdxData?.getIdxEmbeddedPortfolioFeed.idxSettings as IdxSettings) ||
    null;

  // Branding Colors
  const mainColor = idxSettings?.branding.mainColor || "#1b75bb";
  const mainColorLight = lighten(0.4, mainColor);
  const textColor = idxSettings?.branding.textColor || "#334155";
  const textColorLight = lighten(0.4, textColor);
  const backgroundColor = idxSettings?.branding.backgroundColor || "";

  // Derived states for pagination
  const lastPage = useMemo(
    () => Math.ceil(state.total / limit),
    [state.total, limit]
  );

  const allPages = useMemo(
    () => new Array(lastPage || 1).fill(null).map((_, index) => index + 1),
    [lastPage]
  );

  const selectedPage = useMemo(
    () => skip / (idxFeed?.propertiesPerPage || 1) + 1,
    [skip, idxFeed?.propertiesPerPage]
  );

  const filteredPages = useMemo(() => {
    const previousPages = allPages.slice(0, selectedPage - 1);
    const nextPages = allPages.slice(selectedPage, allPages.length);
    const filteredPreviousPages =
      nextPages.length < 3
        ? previousPages.slice(-6 + nextPages.length)
        : previousPages.slice(-3);
    const filteredNextPages =
      previousPages.length < 3
        ? nextPages.slice(0, 6 - previousPages.length)
        : nextPages.slice(0, 3);

    return [...filteredPreviousPages, selectedPage, ...filteredNextPages];
  }, [allPages, selectedPage]);

  // Pagination functions
  const onNextPage = useCallback(() => {
    if (selectedPage === lastPage) return;
    setSkip((currentValue) => currentValue + limit);
  }, [selectedPage, lastPage, limit]);

  const onPreviousPage = useCallback(() => {
    if (selectedPage === 1) return;
    setSkip((currentValue) => currentValue - limit);
  }, [selectedPage, limit]);

  const onGoToPage = useCallback(
    (page: number) => {
      setSkip((page - 1) * limit);
    },
    [limit]
  );

  // Setting the properties per page on initial load
  useEffect(() => {
    if (
      !loadingProperties &&
      properties?.length > 0 &&
      idxFeed?.propertiesPerPage &&
      state.total === 0
    ) {
      setPerPage(idxFeed?.propertiesPerPage);
    }
  }, [idxFeed, state.total, setPerPage, loadingProperties, properties]);

  if (getIdxError || getPropertiesError) {
    if (getPropertiesError?.message === ServerErrors.PORTFOLIO_IS_PRIVATE) {
      return <UnavailablePortfolioPage />;
    }
    return (
      <NotFoundPage
        onRedirect={() =>
          (window.location.href = "https://www.dashtodash.com/")
        }
      />
    );
  }

  // HTML Component
  return (
    <>
      {/* Loading Feed Settings */}
      {loadingSettings && selectedView === "GRID" && <Spinner fullContent />}

      {!loadingSettings && (
        <>
          <div
            className=" text-slate-800 overflow-auto flex flex-col gap-12 p-8"
            style={{ backgroundColor: backgroundColor, color: textColor }}
          >
            {/* View Mode and quantity info */}
            <div className="flex flex-row sm:flex-row items-center justify-end">
              <div className="flex gap-4 items-center">
                <span className="">View Mode:</span>
                <div className="flex items-center rounded-full border p-1 max-w-max">
                  <button
                    onClick={() => {
                      setSelectedView("GRID");
                      setPreviousState(null);
                    }}
                    style={{
                      backgroundColor:
                        selectedView === "GRID" ? mainColor : "transparent",
                      color: selectedView === "GRID" ? "white" : textColor,
                    }}
                    className={`py-2 px-4 rounded-full flex items-center justify-center gap-2`}
                  >
                    <FiGrid /> <span className="text-sm">Grid</span>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedView("MAP");
                      setPreviousState(null);
                    }}
                    style={{
                      backgroundColor:
                        selectedView === "MAP" ? mainColor : "transparent",
                      color: selectedView === "MAP" ? "white" : textColor,
                    }}
                    className={`py-2 px-4 rounded-full flex items-center justify-center gap-2`}
                  >
                    <FiMapPin /> <span className="text-sm">Map</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Empty state */}
            {!loadingProperties && portfolioItems.length < 1 && (
              <div className="flex items-center h-full">
                <p>
                  {broker?.profile.fullName} has not yet added any properties to
                  this Feed.
                </p>
              </div>
            )}
            {/* Loading Feedback */}
            {loadingProperties && selectedView === "GRID" && (
              <div className="h-96 w-full flex items-center justify-center animate-pulse gap-2">
                <FiLoader className="animate-spin text-sky-600" />
                <p className="">Loading properties...</p>
              </div>
            )}

            {/* GRID VIEW */}
            {selectedView === "GRID" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolioItems.map((item) => (
                  <Link
                    key={item._id}
                    // Commented code related to IDX Analytics
                    // onClick={() => {
                    //   registerClickMutation({
                    //     variables: {
                    //       idxId: idxFeedId,
                    //       propertyId: item.property?._id,
                    //     },
                    //   });
                    // }}
                    className="rounded-lg shadow-xl border hover:scale-[1.02] transition-all relative bg-white"
                    to={`/exchange/embedded/${idxFeedId}/property/${item.property?._id}`}
                    style={{ color: textColor }}
                    state={{
                      type: "GRID",
                      pagination: {
                        selectedPage,
                        limit,
                      },
                    }}
                  >
                    <img
                      src={
                        item.property?.slideshowMedia[0]?.files[0]?.publicUrl
                      }
                      className="rounded-t-lg h-48 w-full object-cover"
                    />
                    {/* Relative tags */}
                    <div className="absolute top-4 left-4 flex gap-4">
                      {item.property?.marketArea && (
                        <div
                          className=" text-sm text-white rounded px-3 py-1"
                          style={{ backgroundColor: mainColor }}
                        >
                          <p>{item.property.marketArea.name}</p>
                        </div>
                      )}
                      {item.property?.isMarkedAsVerified && (
                        <div
                          className="text-sm rounded px-3 py-1 shadow"
                          style={{
                            backgroundColor: mainColorLight,
                            color: mainColor,
                          }}
                        >
                          <p>Developer Verified</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4 pb-6 flex flex-col">
                      <h2 className="text-lg font-bold">
                        {item.property?.name}
                      </h2>
                      <div className="flex items-center mt-1 flex-wrap text-sm">
                        {item.property?.priceRange && (
                          <>
                            <p>
                              $
                              {item.property?.priceRange?.min
                                ? (
                                    item.property?.priceRange?.min / 1000000
                                  ).toFixed(1)
                                : 0}
                              M - $
                              {item.property?.priceRange?.max
                                ? (
                                    item.property?.priceRange?.max / 1000000
                                  ).toFixed(1)
                                : 0}
                              M
                            </p>

                            <div
                              className="h-5 w-0.5 mx-2"
                              style={{ backgroundColor: textColorLight }}
                            />
                          </>
                        )}
                        {item.property?.averageBathroomsNumberValue && (
                          <>
                            <p className="flex items-center gap-1">
                              <Icon name="bathroom" size={24} />
                              <span>
                                {`${item.property?.averageBathroomsNumberValue?.min}-${item.property?.averageBathroomsNumberValue?.max}`}
                              </span>
                            </p>
                          </>
                        )}
                        {item.property?.averageBedroomsNumberValue && (
                          <>
                            <div
                              className="h-5 w-0.5 mx-2"
                              style={{ backgroundColor: textColorLight }}
                            />
                            <p className="flex items-center gap-1">
                              <Icon name="bedroom" size={24} />
                              <span>
                                {`${item.property?.averageBedroomsNumberValue?.min}-${item.property?.averageBedroomsNumberValue?.max}`}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                      <span
                        className="h-6 px-2 font-bold rounded text-xs text-white flex items-center w-max capitalize mt-4"
                        style={{
                          backgroundColor: mainColor,
                        }}
                      >
                        {item?.property?.projectStatus
                          ?.toLocaleLowerCase()
                          .split("_")
                          .join(" ")}
                      </span>
                      <p className="flex items-center mt-4">
                        <FiMapPin />
                        <span className="ml-1 truncate text-sm">
                          {item.property?.location?.address?.street},{" "}
                          {item.property?.location?.address?.city},{" "}
                          {item.property?.location?.address?.state},{" "}
                          {item.property?.location?.address?.zipCode}
                        </span>
                      </p>
                      {item.property?.yearBuilt && (
                        <p className="truncate mt-2 text-sm">
                          Completion in {item.property?.yearBuilt}.
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* MAP VIEW */}
            {selectedView === "MAP" && (
              <div className="relative">
                <div className="h-[600px] w-full">
                  <ExchangeMapView
                    branding={{ mainColor, textColor }}
                    properties={properties as Property[]}
                    idxFeedId={idxFeedId}
                    previousMapState={previousMapState}
                    currentState={{
                      type: "MAP",
                      pagination: {
                        selectedPage: Math.floor(selectedPage),
                        limit,
                      },
                    }}
                  />
                </div>
                {loadingProperties && (
                  <div className="absolute top-0 left-0 z-50 w-full h-full bg-black/30 flex items-center justify-center">
                    <FiLoader className="animate-spin text-white text-5xl" />
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <footer className="flex items-center justify-between flex-col-reverse sm:flex-row gap-8">
              <a
                style={{ backgroundColor: mainColor, color: mainColor }}
                className="flex items-center justify-center bg-sky-600 rounded-full py-2 px-4 flex-wrap gap-2 shrink-0 min-w-max"
                href="https://dashtodash.com/"
                target="_blank"
              >
                <span className="text-sm text-white font-[montserrat]">
                  Powered By
                </span>
                <img src={DashLogo} alt="DASH" className="h-4" />
              </a>

              {/* Pagination */}
              {selectedView === "GRID" && (
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <button
                      disabled={selectedPage === 1}
                      className="disabled:opacity-30 h-8 w-8 flex items-center justify-center rounded-lg text-sm border"
                      onClick={onPreviousPage}
                      style={{ borderColor: mainColor, color: mainColor }}
                    >
                      <FiChevronLeft />
                    </button>

                    {filteredPages.map((page) => {
                      return (
                        <button
                          onClick={() => onGoToPage(page)}
                          key={page}
                          style={{
                            backgroundColor:
                              selectedPage === page ? mainColor : "transparent",
                            color: selectedPage === page ? "white" : mainColor,
                            borderColor:
                              selectedPage === page ? "transparent" : mainColor,
                          }}
                          className={`h-8 w-8 flex items-center justify-center rounded-lg text-sm ${
                            selectedPage === page
                              ? ""
                              : "border hover:bg-opacity-10 hover:bg-sky-600"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}

                    <button
                      disabled={selectedPage === lastPage}
                      className="disabled:opacity-30 h-8 w-8 flex items-center justify-center rounded-lg text-sm border"
                      onClick={onNextPage}
                      style={{ borderColor: mainColor, color: mainColor }}
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </footer>
          </div>
        </>
      )}
    </>
  );
}
